// ItemSales.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Text,
  Icon,
  Flex,
  Spinner,
  VStack,
  HStack,
  useColorModeValue,
  Heading,
  IconButton,
  Image,
  useDisclosure,
  Collapse,
  Grid,
  GridItem,
  Skeleton,
  Center,
  Button,
  ButtonGroup,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiShoppingBag,
  FiDollarSign,
  FiTrendingUp,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import BarLineChart from "./BarLineChart";
import ChartSelection from "./ChartSelection";
import DateSelector from "./DateSelector";
import DateRangePicker from "./DateRangePicker";
import moment from "moment";
import API, { getMediaURL } from "../../context/API";
import KeyMetricsSelector from "./KeyMetricSelector";
import CustomersDropDown from "../Loyalty/CutomersDropDown";
import StoresDropDown from "./StoresDropDown";
import { motion } from "framer-motion";
import Pagination from "../Common/Pagination/Pagination";

// Reusable InfoTile Component for Item Sales
const addCommasInPrices = (value) => {
  return parseFloat(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
const MotionBox = motion(Box);

const InfoTile = ({ title, subTitle, icon, merchant, storeId }) => {
  const { isOpen, onToggle } = useDisclosure();

  // Define color constants using useColorModeValue at the top level
  const tileBg = useColorModeValue("gray.100", "gray.700");
  const tileHoverBg = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.500");
  const metricBorderColor = useColorModeValue("gray.200", "gray.600");
  const overlayBg = useColorModeValue(
    "rgba(255, 255, 255, 0.6)",
    "rgba(0, 0, 0, 0.6)"
  );
  const collapseBg = useColorModeValue("gray.50", "gray.600");

  // State for selected filter and item data
  const [selectedFilter, setSelectedFilter] = useState("allTime");
  const [itemData, setItemData] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [isError, setIsError] = useState(null); // Error state
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: moment("2023-01-01"), // Start date is 1st January 2000
    endDate: moment(), // End date is the current date
  });

  // Calculate total revenue
  const value = useMemo(() => {
    return itemData.reduce(
      (sum, item) => sum + parseFloat(item.totalRevenue),
      0
    );
  }, [itemData]);

  // Handle filter selection
  const handleSelection = (selection) => {
    setSelectedDateRange(selection);
  };

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    setIsLoading(true);
    setIsError(null);
    try {
      const params1 = {
        startDate: moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
        endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
        merchantId: merchant?.id,
        store: storeId,
      };
      const token = localStorage.getItem("access_token");
      const res = await API.get("/analytics/item-sales", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params1,
      });
      const itemsRes = res.data.data;
      const sortedItems = [...itemsRes.items].sort(
        (a, b) => b.totalTimesSold - a.totalTimesSold
      );

      // Set itemData to sortedItems
      setItemData(sortedItems);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      setIsError("Failed to load analytics data.");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateRange, merchant, storeId]);

  return (
    <MotionBox
      bg={tileBg}
      borderRadius="md"
      p={{ base: 4, sm: 6, md: 6, lg: 8 }}
      cursor="pointer"
      _hover={{ bg: tileHoverBg }}
      transition="background 0.3s, transform 0.2s"
      onClick={onToggle}
      h="100%" // Make tile occupy full height
      w="100%"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      position="relative" // For spinner overlay
    >
      {/* Date Selector */}
      <HStack justifyContent="flex-end" mb={{ base: 4, sm: 6 }} flexWrap="wrap">
        <DateSelector onDateSelect={handleSelection} />
      </HStack>

      {/* Content Section */}
      <Flex
        align="center"
        justifyContent="space-between"
        flexDirection={{ base: "column", sm: "row" }}
      >
        <HStack spacing={3} mb={{ base: 4, sm: 0 }}>
          {/* Icon */}
          {isLoading ? (
            <Skeleton
              circle
              height={{ base: 8, sm: 10 }}
              width={{ base: 8, sm: 10 }}
            />
          ) : (
            <Icon
              as={icon}
              w={{ base: 8, sm: 10 }}
              h={{ base: 8, sm: 10 }}
              color={textColor}
              aria-label={`${title} icon`}
            />
          )}

          {/* Title and Subtitle */}
          {isLoading ? (
            <VStack align="start" spacing={1} w="100%">
              <Skeleton height="20px" width="70%" />
              <Skeleton height="16px" width="50%" />
            </VStack>
          ) : (
            <VStack align="start" spacing={0}>
              <Text
                fontSize={{ base: "xl", sm: "xl", md: "lg", lg: "xl" }}
                fontWeight="bold"
                color={textColor}
              >
                {title}
              </Text>
              <Text
                fontSize={{ base: "sm", sm: "sm", md: "sm", lg: "sm" }}
                color={subTextColor}
              >
                {subTitle}
              </Text>
            </VStack>
          )}
        </HStack>

        {/* Total Revenue */}
        {isLoading ? (
          <Skeleton height="36px" width="120px" />
        ) : (
          <Text
            fontSize={{ base: "3xl", sm: "3xl", md: "2xl", lg: "3xl" }}
            fontWeight="bold"
            color={textColor}
          >
            $
            {value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        )}
      </Flex>

      {/* Error Message */}
      {isError && (
        <Text fontSize="sm" color="red.500" mt={4}>
          {isError}
        </Text>
      )}

      {/* Collapse Section with Animation */}
      <Collapse in={isOpen} animateOpacity>
        {isLoading ? (
          <VStack align="start" spacing={2} mt={4}>
            <Skeleton height="16px" width="90%" />
            <Skeleton height="16px" width="80%" />
            <Skeleton height="16px" width="85%" />
          </VStack>
        ) : (
          <MotionBox
            mt={4}
            p={4}
            bg={collapseBg}
            borderRadius="md"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.3 }}
          >
            <Text fontSize="sm" color={subTextColor}>
              Detailed information about {title.toLowerCase()} goes here.
            </Text>
          </MotionBox>
        )}
      </Collapse>

      {/* Optional: Loading Spinner Overlay */}
      {isLoading && (
        <Center
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg={overlayBg}
          borderRadius="md"
        >
          <Spinner size="lg" color="teal.500" />
        </Center>
      )}
    </MotionBox>
  );
};

// ItemsRankingGrid Component (using Grid)
const ItemsRankingGrid = ({ data }) => {
  // Define color constants
  const textColor = useColorModeValue("gray.800", "white");
  const gridHeaderBg = useColorModeValue("gray.100", "gray.700");

  return (
    <Box w="100%" h="100%">
      <Grid
        templateColumns="repeat(3, 1fr)"
        gap={4}
        alignItems="center"
        bg={gridHeaderBg}
        p={2}
        borderRadius="md"
      >
        <GridItem colSpan={1}>
          <Text fontWeight="bold" color={textColor}>
            Item
          </Text>
        </GridItem>
        <GridItem>
          <Text fontWeight="bold" color={textColor} textAlign="right">
            Units Sold
          </Text>
        </GridItem>
        <GridItem>
          <Text fontWeight="bold" color={textColor} textAlign="right">
            Revenue ($)
          </Text>
        </GridItem>
      </Grid>
      <Grid
        templateColumns="repeat(3, 1fr)"
        gap={4}
        mt={2}
        h="calc(100% - 40px)"
      >
        {data.slice(0, 5).map((item) => (
          <React.Fragment key={item.id}>
            {/* Item Name with Circular Image */}
            <GridItem>
              <HStack spacing={3}>
                <Box
                  position="relative"
                  borderRadius="full"
                  overflow="hidden"
                  width="40px"
                  height="40px"
                  minWidth={"40px"}
                >
                  <Image
                    src={getMediaURL(item.imageUrl)}
                    alt={item.name}
                    boxSize="40px"
                    objectFit="cover"
                    fallbackSrc="https://via.placeholder.com/40"
                  />
                  {/* Gradient Overlay */}
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    width="100%"
                    height="100%"
                    bgGradient="linear(to-t, rgba(0,0,0,0.2), transparent)"
                  />
                </Box>
                <Text color={textColor}>{item.item}</Text>
              </HStack>
            </GridItem>
            {/* Units Sold */}
            <GridItem>
              <Text color={textColor} textAlign="right">
                {item.totalTimesSold}
              </Text>
            </GridItem>
            {/* Revenue */}
            <GridItem>
              <Text color={textColor} textAlign="right">
                ${addCommasInPrices(parseFloat(item.totalRevenue).toFixed(2))}
              </Text>
            </GridItem>
          </React.Fragment>
        ))}
      </Grid>
    </Box>
  );
};

// ItemSales Component
const ItemSales = () => {
  const [itemData, setItemData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isFilter, setIsFilter] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: moment("2023-01-01"), // Start date is 1st January 2000
    endDate: moment(), // End date is the current date
  });
  const [chartType, setChartType] = useState("line");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [itemsData, setItemsData] = useState(null);
  const [metricOption, setMetricOption] = useState("totalRevenue");
  const [keyMetricsFilter, setKeyMetricsFilter] = useState({});
  const [merchant, setMerchant] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "item", // Default column to sort
    isAscending: true, // Default sort order
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [storeId, setStoreid] = useState("");
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      isAscending: prev.key === key ? !prev.isAscending : true, // Toggle order if the same column is clicked
    }));
  };

  const sortedData = [...itemData].sort((a, b) => {
    const { key, isAscending } = sortConfig;
    const valueA = a[key];
    const valueB = b[key];

    if (typeof valueA === "string") {
      return isAscending
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    }
    return isAscending ? valueA - valueB : valueB - valueA;
  });

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  const location = useLocation();

  // Define color constants
  const textColor = useColorModeValue("gray.800", "white");
  const tableHeaderBg = useColorModeValue("gray.100", "gray.700");
  const iconColor = useColorModeValue("gray.800", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const subTextColor = useColorModeValue("gray.600", "gray.400");
  const chartbg = useColorModeValue("gray.100", "gray.700");

  // Pagination calculations
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const paginatedData = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    const endIdx = startIdx + itemsPerPage;
    return sortedData.slice(startIdx, endIdx);
  }, [sortedData, currentPage, itemsPerPage]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Generate page numbers for pagination controls
  const pageNumbers = useMemo(() => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }, [totalPages]);

  const fetchAnalyticsData = async () => {
    try {
      setIsLoading(true);
      const params1 = {
        filter: selectedFilter,
        merchantId: merchant?.id,
        store: storeId,
      };
      const params2 = {
        startDate: moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
        endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
        merchantId: merchant?.id,
        store: storeId,
      };
      const token = localStorage.getItem("access_token");
      const res = await API.get("/analytics/item-sales", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: isFilter ? params1 : params2,
      });
      const itemsRes = res.data.data.items;
      const totalRevenue =
        itemsRes.length > 0
          ? itemsRes.reduce(
              (sum, category) => sum + parseFloat(category.totalRevenue),
              0
            )
          : 0;
      const averageRevenue = totalRevenue / itemsRes.length || 0;

      // Find best category by revenue
      const bestCategoryByRevenue =
        itemsRes.length > 0
          ? itemsRes.reduce((prev, current) =>
              parseFloat(current.totalRevenue) > parseFloat(prev.totalRevenue)
                ? current
                : prev
            )
          : 0;

      // Find best category by units sold
      const bestCategoryByUnitsSold =
        itemsRes.length > 0
          ? itemsRes.reduce((prev, current) =>
              current.totalTimesSold > prev.totalTimesSold ? current : prev
            )
          : 0;
      setKeyMetricsFilter({
        totalRevenue: totalRevenue.toFixed(2),
        averageRevenue: averageRevenue.toFixed(2),
        bestCategoryByRevenue: parseFloat(
          bestCategoryByRevenue.totalRevenue
        ).toFixed(2),
        bestCategoryByUnitsSold: bestCategoryByUnitsSold.totalTimesSold,
      });
      setItemsData(res.data?.data?.items);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedFilter, selectedDateRange, merchant, storeId]);
  useEffect(() => {
    if (itemsData) {
      // Sort items based on timesSold
      const sortedItems = [...itemsData].sort(
        (a, b) => b.totalTimesSold - a.totalTimesSold
      );

      // Set itemData to sortedItems
      setItemData(sortedItems);

      setLoading(false);
    } else {
      setItemData([]);
      setLoading(false);
    }
  }, [itemsData, storeId]);

  const handleSelection = (selection) => {
    setSelectedDateRange(selection);
    // setSelectedFilter(selection);
    // setIsFilter(true);
  };

  return (
    <Box p={5} color={textColor} minH="100vh">
      {loading ? (
        <Flex justify="center" align="center" minH="80vh">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <VStack spacing={6} align="stretch">
          {/* Header with Back Button */}
          <Flex align="center">
            <IconButton
              icon={<FiArrowLeft />}
              variant="ghost"
              borderRadius={"full"}
              color={iconColor}
              onClick={() => navigate(-1)}
              aria-label="Go Back"
              mr={4}
              size="lg"
            />

            <VStack align="start" spacing={1} width={"100%"}>
              <HStack
                display={"flex"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <Heading size="xl" fontWeight="bold">
                  Item Sales
                </Heading>
                <HStack>
                  {user.role === "admin" && (
                    <HStack>
                      <Text>By Merchants</Text>
                      <CustomersDropDown
                        onOptionSelect={(merch) => setMerchant(merch)}
                      />
                    </HStack>
                  )}
                  <HStack>
                    <Text>By Store</Text>
                    <StoresDropDown
                      onOptionSelect={(store) => setStoreid(store.id)}
                      merchantId={merchant?.id}
                      role={user?.role}
                    />
                  </HStack>
                </HStack>
              </HStack>
              <Text fontSize="md" color={subTextColor}>
                Analyze sales performance by items
              </Text>
            </VStack>
          </Flex>

          {/* Item Sales Tile and Items Ranking Grid */}
          <Flex direction={{ base: "column", md: "row" }} gap={6}>
            {/* Item Sales Tile */}
            <Box flex="1" minH="200px">
              <InfoTile
                title="Total Revenue"
                subTitle="Overall revenue from all items"
                icon={FiDollarSign}
                merchant={merchant}
                storeId={storeId}
              />
            </Box>

            {/* Items Ranking Grid */}
            <Box flex="1" minH="200px">
              <ItemsRankingGrid data={itemData} />
            </Box>
          </Flex>

          {/* Line Chart */}
          <Box borderRadius="md" width={"full"} bg={chartbg} boxShadow="md">
            <Heading size="md" mx={5} mt={4}>
              Revenue Over Time
            </Heading>
            <Box height="450px" width={"full"}>
              <VStack>
                <HStack
                  padding={4}
                  width={"100%"}
                  justifyContent={"flex-start"}
                  wrap={"wrap"}
                >
                  <DateRangePicker
                    selectedDateRange={selectedDateRange}
                    onDateRangeChange={(range) => {
                      setIsFilter(false);
                      setSelectedDateRange(range);
                    }}
                  />
                  <DateSelector onDateSelect={handleSelection} />
                  <KeyMetricsSelector
                    options={[
                      { label: "Revenue", value: "totalRevenue" },
                      { label: "Times Item Sold", value: "totalTimesSold" },
                    ]}
                    onSelectionChange={(option) => setMetricOption(option)}
                  />
                  <ChartSelection onChange={(type) => setChartType(type)} />
                </HStack>
                <HStack
                  paddingX={4}
                  width={"100%"}
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  gap={10}
                  wrap={"wrap"}
                >
                  <Text display={"flex"} alignItems={"center"}>
                    <Icon
                      as={FiTrendingUp}
                      w={5}
                      h={5}
                      color={"green.500"}
                      marginRight={"4px"}
                    />
                    Total :
                    <Text paddingLeft={2} as={"span"}>
                    ${addCommasInPrices(keyMetricsFilter?.totalRevenue)}
                    </Text>
                  </Text>
                  <Text display={"flex"} alignItems={"center"}>
                    <Icon
                      as={FiTrendingUp}
                      w={5}
                      h={5}
                      color={"blue.500"}
                      marginRight={"4px"}
                    />
                    Best Item by Revenue :
                    <Text paddingLeft={2} as={"span"}>
                    ${addCommasInPrices(
                        keyMetricsFilter?.bestCategoryByRevenue
                      )}
                    </Text>
                  </Text>
                  <Text display={"flex"} alignItems={"center"}>
                    <Icon
                      as={FiTrendingUp}
                      w={5}
                      h={5}
                      color={"orange.500"}
                      marginRight={"4px"}
                    />
                    Best Item by Units Sold :
                    <Text paddingLeft={2} as={"span"}>
                      {keyMetricsFilter?.bestCategoryByUnitsSold}
                    </Text>
                  </Text>
                  <Text display={"flex"} alignItems={"center"}>
                    <Icon
                      as={FiTrendingUp}
                      w={5}
                      h={5}
                      color={"purple.500"}
                      marginRight={"4px"}
                    />
                    Average Revenue per Item :
                    <Text paddingLeft={2} as={"span"}>
                    ${addCommasInPrices(keyMetricsFilter?.averageRevenue)}
                    </Text>
                  </Text>
                </HStack>
                <Box width={"100%"} height={"380px"}>
                  <BarLineChart
                    data={itemsData}
                    chartType={chartType}
                    xKey={"item"}
                    yKey={metricOption}
                    valueKey={metricOption}
                    isLoading={isLoading}
                  />
                </Box>
              </VStack>
            </Box>
          </Box>

          {/* Items Sold and Revenue Table */}
          <Box
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            p={4}
            mt={8}
          >
            <Heading size="md" mb={4}>
              Items Sold and Revenue
            </Heading>
            <Box overflowX="auto">
              <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                {/* Table Headers */}
                <Box
                  bg={tableHeaderBg}
                  p={2}
                  borderRadius="md"
                  onClick={() => handleSort("item")}
                  cursor="pointer"
                >
                  <Text
                    display={"flex"}
                    fontWeight="bold"
                    color={textColor}
                    justifyContent={"space-between"}
                  >
                    <Text>Item </Text>
                    {sortConfig.isAscending && sortConfig.key === "item"
                      ? "↑"
                      : "↓"}
                  </Text>
                </Box>
                <Box
                  bg={tableHeaderBg}
                  p={2}
                  borderRadius="md"
                  onClick={() => handleSort("totalTimesSold")}
                  cursor="pointer"
                >
                  <Text
                    display={"flex"}
                    fontWeight="bold"
                    color={textColor}
                    justifyContent={"space-between"}
                  >
                    <Text>Units Sold </Text>
                    {sortConfig.isAscending &&
                    sortConfig.key === "totalTimesSold"
                      ? "↑"
                      : "↓"}
                  </Text>
                </Box>
                <Box
                  bg={tableHeaderBg}
                  p={2}
                  borderRadius="md"
                  onClick={() => handleSort("totalRevenue")}
                  cursor="pointer"
                >
                  <Text
                    display={"flex"}
                    fontWeight="bold"
                    color={textColor}
                    justifyContent={"space-between"}
                  >
                    <Text>Revenue ($) </Text>
                    {sortConfig.isAscending && sortConfig.key === "totalRevenue"
                      ? "↑"
                      : "↓"}
                  </Text>
                </Box>

                {/* Table Rows */}
                {paginatedData.map((item) => (
                  <>
                    <Box p={2} borderBottom="1px" borderColor={borderColor}>
                      <HStack spacing={3}>
                        <Box
                          position="relative"
                          borderRadius="full"
                          overflow="hidden"
                          width="40px"
                          height="40px"
                        >
                          <Image
                            src={getMediaURL(item.imageUrl)}
                            alt={item.name}
                            boxSize="40px"
                            objectFit="cover"
                            fallbackSrc="https://via.placeholder.com/40"
                          />
                          <Box
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            height="100%"
                            bgGradient="linear(to-t, rgba(0,0,0,0.2), transparent)"
                          />
                        </Box>
                        <Text color={textColor}>{item.item}</Text>
                      </HStack>
                    </Box>
                    <Box p={2} borderBottom="1px" borderColor={borderColor}>
                      <Text color={textColor} textAlign="right">
                        {item.totalTimesSold}
                      </Text>
                    </Box>
                    <Box p={2} borderBottom="1px" borderColor={borderColor}>
                      <Text color={textColor} textAlign="right">
                        $
                        {addCommasInPrices(
                          parseFloat(item.totalRevenue).toFixed(2)
                        )}
                      </Text>
                    </Box>
                  </>
                ))}
              </Grid>
              <HStack justifyContent="center" mt={4}>
                <Pagination
                  currentPage={currentPage}
                  itemsPerPage={totalItems}
                  totalPages={totalPages}
                  onItemsPerPageChange={(lim) => setItemsPerPage(lim)}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </HStack>
            </Box>
          </Box>
        </VStack>
      )}
    </Box>
  );
};

export default ItemSales;
